import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE } from './config';

export interface User {
  id: string;
  email: string;
  fullName: string;
  role: 'admin' | 'user-full' | 'user-reader' | 'full' | 'reader';
  approved?: boolean;
  stocks: string[];
  createdAt?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, fullName: string, password: string) => Promise<{ message?: string }>;
  logout: () => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
  updateStocks: (stocks: string[]) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function authFetch(endpoint: string, token: string | null, options: RequestInit = {}) {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return data;
}

function normalizeUser(user: Partial<User> | null | undefined): User | null {
  if (!user || !user.id || !user.email) return null;

  const fallbackName = user.email.split('@')[0] || 'User';

  return {
    id: user.id,
    email: user.email,
    fullName: (user.fullName || fallbackName).trim(),
    role: (user.role || 'user-reader') as User['role'],
    approved: user.approved,
    stocks: Array.isArray(user.stocks) ? user.stocks : [],
    createdAt: user.createdAt,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const data = await authFetch('/auth/profile', token);
      setUser(normalizeUser(data));
    } catch (err) {
      // Only log out if the token was explicitly rejected (401/403).
      // Transient errors (network, 5xx, cold start) must NOT clear the session.
      const status = (err as { status?: number })?.status;
      if (status === 401 || status === 403) {
        logout();
      } else {
        console.warn('Profile refresh failed (keeping session):', err);
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => { refreshProfile(); }, [refreshProfile]);

  const login = async (email: string, password: string) => {
    const data = await authFetch('/auth/login', null, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    localStorage.setItem('auth_token', data.token);
    setToken(data.token);
    setUser(normalizeUser(data.user));
  };

  const register = async (email: string, fullName: string, password: string) => {
    const data = await authFetch('/auth/register', null, {
      method: 'POST',
      body: JSON.stringify({ email, fullName, password }),
    });
    if (data.token && data.user) {
      localStorage.setItem('auth_token', data.token);
      setToken(data.token);
      setUser(normalizeUser(data.user));
    }
    return data;
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    await authFetch('/auth/change-password', token, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  };

  const deleteAccount = async (password: string) => {
    await authFetch('/auth/delete-account', token, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
    logout();
  };

  const updateStocks = async (stocks: string[]) => {
    const data = await authFetch('/auth/update-stocks', token, {
      method: 'POST',
      body: JSON.stringify({ stocks }),
    });
    if (user) setUser({ ...user, stocks: data.stocks });
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, changePassword, deleteAccount, updateStocks, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
